import { Space, Badge } from "antd";
import { useIntl } from "react-intl";
import {
  DescriptionText,
  StatusBadge,
  StyledTable,
  StyledTabs,
} from "./styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChecklistDrawer from "../CheckListDrawer";
import { useDispatch, useSelector } from "react-redux";
import { solveChecklistStep } from "../../../redux/modules/checklist/actions";
import dayjs from "dayjs";

const STATUS = {
  CONCLUDED: "CONCLUDED",
  PENDING: "PENDING",
  DELAYED: "DELAYED",
  OVERDUE: "OVERDUE",
  ON_TRACK: "ON_TRACK",
};

const ChecklistTable = ({
  items = [],
  onTableChange,
  isHomepage = false,
  onStatusChanged,
}) => {
  const intl = useIntl();

  const [tableData, setTableData] = useState([]);
  const [loadingRows, setLoadingRows] = useState([]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeKey, setActiveKey] = useState("1");
  const [sortedInfo, setSortedInfo] = useState({});
  const dispatch = useDispatch();
  const normalizeStatus = (status) => status?.toUpperCase?.() || "";
  const { event } = useSelector((state) => state.initialSettings);
  const isPt = intl.locale.startsWith("pt");
  const itemsWasCleared = useRef(false);

  useEffect(() => {
    if (!items?.length) {
      itemsWasCleared.current = true;
      return;
    }

    setTableData((prev) => {
      const prevIds = prev.map(
        (item) => item.userChecklistId || item.eventChecklistId
      );

      const newIds = items.map(
        (item) => item.userChecklistId || item.eventChecklistId
      );

      const hasDifferentOrder =
        JSON.stringify(prevIds) !== JSON.stringify(newIds);

      if (hasDifferentOrder || prev.length !== items.length) {
        itemsWasCleared.current = false;
        return items;
      }

      const shouldApplyStatusChange = isHomepage || itemsWasCleared.current;

      if (shouldApplyStatusChange) {
        if (itemsWasCleared.current) itemsWasCleared.current = false;
        const hasStatusChange = items.some((newItem) => {
          const newId = newItem.userChecklistId || newItem.eventChecklistId;
          const oldItem = prev.find(
            (p) => (p.userChecklistId || p.eventChecklistId) === newId
          );
          return oldItem && oldItem.status !== newItem.status;
        });
        if (hasStatusChange) return items;
      }

      return prev;
    });
  }, [items, isHomepage]);

  const formatDate = (date) => {
    if (!date) return "-";
    // Datas "YYYY-MM-DD" são interpretadas como UTC por `new Date`, o que recua
    // um dia em fusos negativos (UTC-3). dayjs faz o parsing em horário local.
    return dayjs(date).toDate().toLocaleDateString(intl.locale);
  };

  const statusMap = {
    CONCLUDED: {
      label: intl.formatMessage({ id: "concluded" }),
      variant: "concluded",
    },
    PENDING: {
      label: intl.formatMessage({ id: "on_time" }),
      variant: "on_time",
    },
    ON_TRACK: {
      label: intl.formatMessage({ id: "on_time" }),
      variant: "on_time",
    },
    DELAYED: {
      label: intl.formatMessage({ id: "checklist.delayed" }),
      variant: "delayed",
    },
    OVERDUE: {
      label: intl.formatMessage({ id: "checklist.delayed" }),
      variant: "delayed",
    },
  };

  const getDescription = (record) => {
    return (
      record.title ||
      (isPt ? record.descriptionPt : record.descriptionEn) ||
      record.descriptionPt ||
      record.descriptionEn ||
      "-"
    );
  };

  const handleTableChange = useCallback(
    (_, __, sorter) => {
      setSortedInfo(sorter);

      if (onTableChange && sorter.field) {
        const sortOrder =
          sorter.order === "ascend"
            ? "ASC"
            : sorter.order === "descend"
            ? "DESC"
            : null;

        onTableChange({
          sortBy: sorter.field,
          sortDirection: sortOrder,
        });
      }
    },
    [onTableChange]
  );

  const handleToggleTask = useCallback(
    async (record) => {
      const rowId = record.userChecklistId || record.eventChecklistId;

      const normalized = normalizeStatus(record.status);

      const solved = normalized === STATUS.CONCLUDED ? false : true;

      if (!isHomepage) {
        setTableData((prev) =>
          prev.map((item) => {
            const itemId = item.userChecklistId || item.eventChecklistId;
            if (itemId !== rowId) return item;
            return { ...item, status: solved ? STATUS.CONCLUDED : STATUS.PENDING };
          })
        );
      }

      try {
        setLoadingRows((prev) => [...prev, rowId]);

        await dispatch(
          solveChecklistStep({
            eventChecklistId: record.eventChecklistId,
            userChecklistId: record.userChecklistId,
            solved,
            selectedEvent: event?.value,
            callbackSuccess: isHomepage ? onStatusChanged : undefined,
          })
        );
      } catch (error) {
        console.error(error);
        if (!isHomepage) {
          setTableData((prev) =>
            prev.map((item) => {
              const itemId = item.userChecklistId || item.eventChecklistId;
              if (itemId !== rowId) return item;
              return { ...item, status: normalized };
            })
          );
        }
      } finally {
        setLoadingRows((prev) => prev.filter((id) => id !== rowId));
      }
    },
    [dispatch, event?.value]
  );

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: "items" }),
        key: "position",
        dataIndex: "position",
        sorter: true,

        sortOrder: sortedInfo.field === "position" ? sortedInfo.order : null,
        render: (_, record) => (
          <DescriptionText>{getDescription(record)}</DescriptionText>
        ),
      },
      {
        title: intl.formatMessage({ id: "deadline" }),
        key: "expiresIn",
        dataIndex: "expiresIn",
        width: 125,
        sorter: true,
        sortOrder: sortedInfo.field === "expiresIn" ? sortedInfo.order : null,
        render: (_, record) => formatDate(record.dueDate || record.expiresIn),
      },
      {
        title: intl.formatMessage({ id: "status" }),
        dataIndex: "status",
        key: "status",
        width: 99,
        sorter: true,
        sortOrder: sortedInfo.field === "status" ? sortedInfo.order : null,
        render: (status) => {
          const normalized = normalizeStatus(status);

          const mapped = statusMap[normalized] || {
            label: status || "-",
            variant: "default",
          };

          return (
            <StatusBadge variant={mapped.variant}>{mapped.label}</StatusBadge>
          );
        },
      },
      {
        title: "",
        key: "action",
        width: 124,
        render: (_, record) => (
          <a onClick={() => handleOpenDrawer(record)}>
            {intl.formatMessage({ id: "see_details" })}
          </a>
        ),
      },
    ],
    [intl, sortedInfo]
  );

  const handleOpenDrawer = (record) => {
    setSelectedTask(record);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTask(null);
  };

  const counts = useMemo(() => {
    return {
      all: tableData.length,
      delayed: tableData.filter(
        (item) => normalizeStatus(item.status) === STATUS.DELAYED
      ).length,
      onTime: tableData.filter(
        (item) => normalizeStatus(item.status) === STATUS.PENDING
      ).length,
      concludeds: tableData.filter(
        (item) => normalizeStatus(item.status) === STATUS.CONCLUDED
      ).length,
    };
  }, [tableData]);

  const renderLabel = (id, count, isInternalActive) => (
    <Space>
      {intl.formatMessage({ id })}
      <Badge
        count={count}
        style={{
          backgroundColor: isInternalActive
            ? "rgba(231, 246, 251, 1)"
            : "rgba(240, 240, 240, 1)",
          color: isInternalActive
            ? "rgba(17, 167, 217, 1)"
            : "rgba(0, 0, 0, 0.45)",
          boxShadow: "none",
        }}
      />
    </Space>
  );

  const tabs = [
    {
      key: "1",
      label: renderLabel("all_tasks", counts.all, activeKey === "1"),
    },
    {
      key: "2",
      label: renderLabel("delayed", counts.delayed, activeKey === "2"),
    },
    {
      key: "3",
      label: renderLabel("on_time", counts.onTime, activeKey === "3"),
    },
    {
      key: "4",
      label: renderLabel("concludeds", counts.concludeds, activeKey === "4"),
    },
  ];

  const filteredData = useMemo(() => {
    switch (activeKey) {
      case "2":
        return tableData.filter(
          (item) => normalizeStatus(item.status) === STATUS.DELAYED
        );

      case "3":
        return tableData.filter(
          (item) => normalizeStatus(item.status) === STATUS.PENDING
        );

      case "4":
        return tableData.filter(
          (item) => normalizeStatus(item.status) === STATUS.CONCLUDED
        );

      default:
        return tableData;
    }
  }, [activeKey, tableData]);

  return (
    <>
      {!isHomepage && (
        <StyledTabs
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key)}
          items={tabs}
        />
      )}

      <StyledTable
        tableLayout="fixed"
        rowSelection={{
          hideSelectAll: true,

          selectedRowKeys: filteredData
            .filter((item) => normalizeStatus(item.status) === STATUS.CONCLUDED)
            .map((item) => item.userChecklistId || item.eventChecklistId),

          onSelect: async (record) => {
            await handleToggleTask(record);
          },
        }}
        dataSource={isHomepage ? tableData : filteredData}
        columns={columns}
        pagination={false}
        rowKey={(record) => record.userChecklistId || record.eventChecklistId}
        loading={loadingRows.length > 0}
        onChange={handleTableChange}
        rowClassName={(record) =>
          normalizeStatus(record.status) === STATUS.CONCLUDED
            ? "row-completed"
            : ""
        }
      />

      <ChecklistDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        task={selectedTask}
        intl={intl}
        isHomepage={isHomepage}
        onStatusChanged={onStatusChanged}
        onStatusChange={(updatedTask) => {
          if (!isHomepage) {
            setTableData((prev) =>
              prev.map((item) => {
                const itemId = item.userChecklistId || item.eventChecklistId;
                const updatedId =
                  updatedTask.userChecklistId || updatedTask.eventChecklistId;
                if (itemId !== updatedId) return item;
                return updatedTask;
              })
            );
          }
          setSelectedTask(updatedTask);
        }}
      />
    </>
  );
};

export default ChecklistTable;
